import { useCallback, useEffect, useRef, useState } from 'react';

import { AccordionScrollConfig, AccordionScrollState, AccordionTabEvent } from '@shared/types/accordion';
import {
  findAccordionContent,
  findActiveAccordionTab,
  isElementBelowFold,
  scrollToElement,
  waitForAnimationComplete,
} from '@shared/utils/accordionScrollUtils';

const DEFAULT_CONFIG: Required<AccordionScrollConfig> = {
  scrollOffset: 20,
  animationTimeout: 500,
  enabled: true,
  checkDelay: 100,
};

/**
 * Custom hook for handling accordion auto-scroll functionality
 * Automatically scrolls to show expanded accordion content when it extends below the viewport
 *
 * @param config - Configuration options for scroll behavior
 * @returns Object containing event handlers for accordion tabs
 */
export function useAccordionAutoScroll(config: AccordionScrollConfig = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<AccordionScrollState>({
    activeTabIndex: null,
    isScrolling: false,
    timeoutId: null,
  });

  const cleanup = useCallback(() => {
    if (state.timeoutId) {
      clearTimeout(state.timeoutId);
    }
    setState((prev) => ({
      ...prev,
      isScrolling: false,
      timeoutId: null,
    }));
  }, [state.timeoutId]);

  const handleScrollCheck = useCallback(
    async (_tabIndex: number) => {
      if (!mergedConfig.enabled || !accordionRef.current) {
        return;
      }
      try {
        const activeTab = findActiveAccordionTab(accordionRef.current);

        if (!activeTab) {
          return;
        }

        const contentElement = findAccordionContent(activeTab);
        if (!contentElement) {
          return;
        }

        await waitForAnimationComplete(
          contentElement,
          'p-toggleable-content-enter-done',
          mergedConfig.animationTimeout,
        );

        // Add a small delay to ensure the DOM has fully updated after animation
        await new Promise((resolve) => setTimeout(resolve, 50));

        if (isElementBelowFold(contentElement)) {
          setState((prev) => ({ ...prev, isScrolling: true }));
          scrollToElement(contentElement, mergedConfig.scrollOffset);

          // Reset scrolling state after a delay
          const timeoutId = setTimeout(() => {
            setState((prev) => ({ ...prev, isScrolling: false, timeoutId: null }));
          }, 1000);

          setState((prev) => ({ ...prev, timeoutId }));
        }
      } catch (error) {
        console.warn('Accordion auto-scroll error:', error);
      }
    },
    [mergedConfig, accordionRef],
  );

  const handleTabOpen = useCallback(
    (event: AccordionTabEvent) => {
      if (!mergedConfig.enabled) {
        return;
      }

      cleanup();

      setState((prev) => ({ ...prev, activeTabIndex: event.index }));

      const timeoutId = setTimeout(async () => {
        try {
          await handleScrollCheck(event.index);
          setState((prev) => ({ ...prev, timeoutId }));
        } catch (error) {
          console.warn('Error in accordion scroll check:', error);
          setState((prev) => ({ ...prev, timeoutId }));
        }
      }, mergedConfig.checkDelay);
    },
    [mergedConfig.enabled, mergedConfig.checkDelay, cleanup, handleScrollCheck],
  );

  const handleTabClose = useCallback(
    (event: AccordionTabEvent) => {
      cleanup();
      setState((prev) => ({
        ...prev,
        activeTabIndex: prev.activeTabIndex === event.index ? null : prev.activeTabIndex,
      }));
    },
    [cleanup],
  );

  const setAccordionRef = useCallback((element: HTMLDivElement | null) => {
    accordionRef.current = element;
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    handleTabOpen,
    handleTabClose,
    setAccordionRef,
    isScrolling: state.isScrolling,
    activeTabIndex: state.activeTabIndex,
  };
}
